import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { CallbackModal } from '../components/CallbackModal';
import { CheckCircle2, Calendar, MessageCircle, ArrowLeft, PoundSterling, Play, Sparkles, ArrowRight, Clock, Heart, ShieldCheck, Languages, MapPin, Phone, Plus, Minus } from 'lucide-react';
import { useTranslation } from '../i18n';
import { CallbackForm } from '../components/CallbackForm';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const serviceToPriceAnchor: Record<string, string> = {
  fillings: 'fillings-restorations',
  'root-canal': 'root-canal',
  crowns: 'crowns-veneers',
  hygiene: 'dental-hygiene',
  whitening: 'whitening',
  extraction: 'extractions',
  'smile-makeover': 'crowns-veneers',
  cosmetic: 'injectables'
};
type ServiceDetail = {
  intro: {
    en: string;
    ua: string;
    ru: string;
  };
  included: {
    en: string[];
    ua: string[];
    ru: string[];
  };
  time: {
    en: string;
    ua: string;
    ru: string;
  };
  comfort: {
    en: string;
    ua: string;
    ru: string;
  };
  aftercare: {
    en: string;
    ua: string;
    ru: string;
  };
};
const serviceDetails: Record<string, ServiceDetail> = {
  fillings: {
    intro: {
      en: 'Tooth-coloured fillings restore teeth affected by decay or small fractures, keeping a natural look.',
      ua: 'Фотополімерні пломби відновлюють зуби, уражені карієсом або невеликими тріщинами, зберігаючи природний вигляд.',
      ru: 'Фотополимерные пломбы восстанавливают зубы, пораженные кариесом или небольшими трещинами, сохраняя естественный вид.'
    },
    included: {
      en: ['Clinical assessment (+ X-ray if needed)', 'Gentle decay removal and isolation', 'Layered composite restoration', 'Bite adjustment and polishing'],
      ua: ['Клінічна оцінка (+ рентген за потреби)', 'Делікатне видалення карієсу та ізоляція', 'Пошарова композитна реставрація', 'Корекція прикусу та полірування'],
      ru: ['Клиническая оценка (+ рентген при необходимости)', 'Деликатное удаление кариеса и изоляция', 'Послойная композитная реставрация', 'Коррекция прикуса и полировка']
    },
    time: {
      en: '30–60 minutes',
      ua: '30–60 хвилин',
      ru: '30–60 минут'
    },
    comfort: {
      en: 'Local anaesthetic available; minimally invasive approach.',
      ua: 'Місцева анестезія; мінімально інвазивний підхід.',
      ru: 'Местная анестезия; минимально инвазивный подход.'
    },
    aftercare: {
      en: 'Avoid hard biting until numbness wears off; contact us if the bite feels "high".',
      ua: 'Уникайте твердої їжі до зникнення оніміння; зверніться до нас, якщо прикус здається "високим".',
      ru: 'Избегайте твёрдой пищи до исчезновения онемения; свяжитесь с нами, если прикус кажется "высоким".'
    }
  },
  'root-canal': {
    intro: {
      en: 'Aesthetic restorations rebuild tooth shape, edges and colour for a harmonious smile.',
      ua: 'Естетичні реставрації відновлюють форму, краї та колір зуба для гармонійної посмішки.',
      ru: 'Эстетические реставрации восстанавливают форму, края и цвет зуба для гармоничной улыбки.'
    },
    included: {
      en: ['Smile assessment and shade selection', 'Layered composite sculpting', 'High-gloss polishing for natural enamel shine'],
      ua: ['Оцінка посмішки та підбір відтінку', 'Пошарове моделювання композитом', 'Полірування до блиску натуральної емалі'],
      ru: ['Оценка улыбки и подбор оттенка', 'Послойное моделирование композитом', 'Полировка до блеска натуральной эмали']
    },
    time: {
      en: '45–90 minutes per tooth',
      ua: '45–90 хвилин на зуб',
      ru: '45–90 минут на зуб'
    },
    comfort: {
      en: 'Usually comfortable; anaesthetic if required.',
      ua: 'Зазвичай комфортно; анестезія за потреби.',
      ru: 'Обычно комфортно; анестезия при необходимости.'
    },
    aftercare: {
      en: 'Reduce staining foods/drinks for 24–48h; keep regular hygiene visits.',
      ua: 'Обмежте фарбуючі продукти/напої на 24–48 год; відвідуйте гігієніста регулярно.',
      ru: 'Ограничьте красящие продукты/напитки на 24–48 ч; посещайте гигиениста регулярно.'
    }
  },
  crowns: {
    intro: {
      en: 'Crowns protect weakened teeth; veneers enhance colour and form with a refined, natural result.',
      ua: 'Коронки захищають ослаблені зуби; вініри покращують колір та форму з витонченим, природним результатом.',
      ru: 'Коронки защищают ослабленные зубы; виниры улучшают цвет и форму с утончённым, естественным результатом.'
    },
    included: {
      en: ['Consultation + digital planning', 'Scan/impressions and lab fabrication', 'Try-in, bite check, cementation', 'Aftercare guidance'],
      ua: ['Консультація + цифрове планування', 'Сканування/зліпки та виготовлення в лабораторії', 'Примірка, перевірка прикусу, цементування', 'Рекомендації по догляду'],
      ru: ['Консультация + цифровое планирование', 'Сканирование/слепки и изготовление в лаборатории', 'Примерка, проверка прикуса, цементирование', 'Рекомендации по уходу']
    },
    time: {
      en: 'Typically 2 visits',
      ua: 'Зазвичай 2 візити',
      ru: 'Обычно 2 визита'
    },
    comfort: {
      en: 'Local anaesthetic; sensitivity managed carefully.',
      ua: 'Місцева анестезія; чутливість контролюється ретельно.',
      ru: 'Местная анестезия; чувствительность контролируется тщательно.'
    },
    aftercare: {
      en: 'Avoid very hard foods initially; night guard recommended if clenching.',
      ua: 'Спочатку уникайте дуже твердої їжі; нічна капа рекомендована при бруксизмі.',
      ru: 'Сначала избегайте очень твёрдой пищи; ночная капа рекомендована при бруксизме.'
    }
  },
  hygiene: {
    intro: {
      en: 'Deep cleaning removes plaque and tartar to protect gums and improve breath freshness.',
      ua: 'Глибока чистка видаляє наліт та зубний камінь для захисту ясен та свіжості дихання.',
      ru: 'Глубокая чистка удаляет налёт и зубной камень для защиты дёсен и свежести дыхания.'
    },
    included: {
      en: ['Gum screening', 'Scaling and polishing (AirFlow if offered)', 'Personalised home-care plan'],
      ua: ['Скринінг ясен', 'Скейлінг та полірування (AirFlow за наявності)', 'Індивідуальний план домашнього догляду'],
      ru: ['Скрининг дёсен', 'Скейлинг и полировка (AirFlow при наличии)', 'Индивидуальный план домашнего ухода']
    },
    time: {
      en: '30–60 minutes',
      ua: '30–60 хвилин',
      ru: '30–60 минут'
    },
    comfort: {
      en: 'Gentle technique; numbing gel if sensitive.',
      ua: 'Делікатна техніка; знеболюючий гель при чутливості.',
      ru: 'Деликатная техника; обезболивающий гель при чувствительности.'
    },
    aftercare: {
      en: 'Mild gum tenderness possible; follow interdental cleaning routine.',
      ua: 'Можлива легка чутливість ясен; дотримуйтесь міжзубної чистки.',
      ru: 'Возможна лёгкая чувствительность дёсен; соблюдайте межзубную чистку.'
    }
  },
  whitening: {
    intro: {
      en: 'Professional whitening brightens the smile safely with controlled protocols.',
      ua: 'Професійне відбілювання безпечно освітлює посмішку за контрольованими протоколами.',
      ru: 'Профессиональное отбеливание безопасно осветляет улыбку по контролируемым протоколам.'
    },
    included: {
      en: ['Suitability check and shade assessment', 'In-clinic whitening and/or home plan', 'Sensitivity management advice'],
      ua: ['Перевірка придатності та оцінка відтінку', 'Клінічне відбілювання та/або домашній план', 'Поради щодо управління чутливістю'],
      ru: ['Проверка пригодности и оценка оттенка', 'Клиническое отбеливание и/или домашний план', 'Советы по управлению чувствительностью']
    },
    time: {
      en: '60–90 minutes in clinic or 1–2 weeks at home',
      ua: '60–90 хвилин у клініці або 1–2 тижні вдома',
      ru: '60–90 минут в клинике или 1–2 недели дома'
    },
    comfort: {
      en: 'Controlled protocols; sensitivity tips provided.',
      ua: 'Контрольовані протоколи; надаються поради щодо чутливості.',
      ru: 'Контролируемые протоколы; предоставляются советы по чувствительности.'
    },
    aftercare: {
      en: '"White diet" 24–48h; limit coffee/tea/red wine; follow sensitivity tips.',
      ua: '"Біла дієта" 24–48 год; обмежте каву/чай/червоне вино; дотримуйтесь порад.',
      ru: '"Белая диета" 24–48 ч; ограничьте кофе/чай/красное вино; следуйте советам.'
    }
  },
  extraction: {
    intro: {
      en: 'Gentle tooth removal when a tooth cannot be saved, with a focus on safe healing and comfort.',
      ua: 'Делікатне видалення зуба, коли його неможливо зберегти, з акцентом на безпечне загоєння та комфорт.',
      ru: 'Деликатное удаление зуба, когда его невозможно сохранить, с акцентом на безопасное заживление и комфорт.'
    },
    included: {
      en: ['Examination + X-ray', 'Local anaesthetic', 'Extraction + haemostasis', 'Clear post-op instructions'],
      ua: ['Огляд + рентген', 'Місцева анестезія', 'Видалення + гемостаз', 'Чіткі післяопераційні інструкції'],
      ru: ['Осмотр + рентген', 'Местная анестезия', 'Удаление + гемостаз', 'Чёткие послеоперационные инструкции']
    },
    time: {
      en: '20–60 minutes',
      ua: '20–60 хвилин',
      ru: '20–60 минут'
    },
    comfort: {
      en: 'Strong local anaesthetic; supportive approach for anxious patients.',
      ua: 'Сильна місцева анестезія; підтримуючий підхід для тривожних пацієнтів.',
      ru: 'Сильная местная анестезия; поддерживающий подход для тревожных пациентов.'
    },
    aftercare: {
      en: 'Soft food, no smoking 48–72h, cold compress day 1; contact us if severe pain/swelling.',
      ua: "М'яка їжа, не палити 48–72 год, холодний компрес 1-й день; зверніться при сильному болю/набряку.",
      ru: 'Мягкая пища, не курить 48–72 ч, холодный компресс 1-й день; обратитесь при сильной боли/отёке.'
    }
  },
  cosmetic: {
    intro: {
      en: 'Non-surgical aesthetic treatments focused on natural, refreshed results.',
      ua: 'Нехірургічні естетичні процедури, спрямовані на природний, свіжий результат.',
      ru: 'Нехирургические эстетические процедуры, направленные на естественный, свежий результат.'
    },
    included: {
      en: ['Medical assessment + contraindications', 'Treatment plan and consent', 'Aftercare instructions'],
      ua: ['Медична оцінка + протипоказання', 'План лікування та згода', 'Інструкції по догляду'],
      ru: ['Медицинская оценка + противопоказания', 'План лечения и согласие', 'Инструкции по уходу']
    },
    time: {
      en: '20–45 minutes',
      ua: '20–45 хвилин',
      ru: '20–45 минут'
    },
    comfort: {
      en: 'Only by qualified medical professional; safety-first approach.',
      ua: 'Тільки кваліфікованим медичним спеціалістом; підхід "безпека перш за все".',
      ru: 'Только квалифицированным медицинским специалистом; подход "безопасность прежде всего".'
    },
    aftercare: {
      en: 'Avoid intense exercise/sauna for 24h; follow provided guidance.',
      ua: 'Уникайте інтенсивних вправ/сауни протягом 24 год; дотримуйтесь наданих рекомендацій.',
      ru: 'Избегайте интенсивных упражнений/сауны в течение 24 ч; следуйте предоставленным рекомендациям.'
    }
  }
};
// UA-optimised H1 titles with location keywords
const uaServiceTitles: Record<string, string> = {
  fillings: 'Пломбування зубів у Лондоні (Angel)',
  'root-canal': 'Лікування каналів у Лондоні (Angel)',
  crowns: 'Коронки та протезування в Лондоні (Angel)',
  hygiene: 'Професійна гігієна зубів у Лондоні (Angel)',
  whitening: 'Відбілювання зубів у Лондоні (Angel)',
  extraction: 'Видалення зубів у Лондоні (Angel)',
  'smile-makeover': 'Smile Makeover у Лондоні — Dentico Clinic (Angel)',
  cosmetic: "Ін'єкційна косметологія в Лондоні (Angel)"
};
// Related services mapping
const relatedServices: Record<string, string[]> = {
  fillings: ['root-canal', 'crowns', 'hygiene'],
  'root-canal': ['fillings', 'crowns', 'extraction'],
  crowns: ['fillings', 'smile-makeover', 'whitening'],
  hygiene: ['whitening', 'fillings', 'smile-makeover'],
  whitening: ['hygiene', 'smile-makeover', 'crowns'],
  extraction: ['root-canal', 'crowns', 'fillings'],
  'smile-makeover': ['whitening', 'crowns', 'cosmetic'],
  cosmetic: ['smile-makeover', 'whitening', 'hygiene']
};
// Mini FAQ per service (UA only)
const serviceUaFaqs: Record<string, {
  q: string;
  a: string;
}[]> = {
  fillings: [{
    q: 'Скільки коштує пломба в Лондоні?',
    a: 'Ціна залежить від розміру та глибини карієсу і матеріалу. Після огляду ми озвучимо вартість і варіанти лікування.'
  }, {
    q: 'Чи боляче лікувати карієс?',
    a: 'У більшості випадків лікування проводиться під анестезією і є комфортним. Ми пояснюємо кожен етап і працюємо максимально делікатно.'
  }, {
    q: 'Як зрозуміти, що карієс потрібно лікувати?',
    a: 'Ознаки: чутливість, біль при солодкому/холодному, потемніння, застрягання їжі. Точний діагноз визначається на огляді.'
  }, {
    q: 'Чи можна зробити пломбу в колір зуба?',
    a: 'Так. Ми використовуємо сучасні композитні матеріали, які підбираються під відтінок зуба для природного вигляду.'
  }, {
    q: 'Де ви знаходиться?',
    a: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington).'
  }],
  'root-canal': [{
    q: 'Коли потрібно лікувати канали?',
    a: 'Коли є запалення нерва або інфекція в кореневих каналах. Часті симптоми: сильний біль, чутливість, набряк, біль при накусуванні.'
  }, {
    q: 'Чи боляче лікувати канали?',
    a: 'Лікування проводиться під анестезією. У більшості випадків процедура безболісна, а після можливий тимчасовий дискомфорт.'
  }, {
    q: 'Скільки коштує лікування каналів у Лондоні?',
    a: 'Вартість залежить від кількості каналів та складності випадку. Після огляду ми надаємо план лікування і кошторис.'
  }, {
    q: 'Скільки візитів потрібно?',
    a: 'Іноді лікування можна завершити за 1 візит, але складні випадки можуть потребувати 2 візитів. Це визначається після діагностики.'
  }, {
    q: 'Чи робите термінові візити при гострому болю?',
    a: "Так. Якщо у вас сильний біль, зв'яжіться з нами якнайшвидше — ми постараємося знайти найближчий час."
  }],
  crowns: [{
    q: 'Коли потрібна коронка на зуб?',
    a: 'Коронка потрібна, коли зуб сильно зруйнований, після лікування каналів або коли потрібне довготривале відновлення форми та функції.'
  }, {
    q: 'Скільки коштує коронка в Лондоні?',
    a: 'Вартість залежить від матеріалу та складності випадку. Після огляду ми складаємо план лікування і кошторис.'
  }, {
    q: 'Скільки часу займає встановлення коронки?',
    a: 'Зазвичай потрібно 2 візити: підготовка зуба та зняття відбитків/скан, потім фіксація коронки. Терміни залежать від клінічної ситуації.'
  }, {
    q: 'Чи боляче ставити коронку?',
    a: 'Процедура проводиться під анестезією. Дискомфорт мінімальний, а після — можливі короткочасні відчуття адаптації.'
  }, {
    q: 'Де знаходиться Dentico Clinic?',
    a: 'Angel / Islington, Central London (EC1V). 360–364 City Road, London EC1V 2PY.'
  }],
  hygiene: [{
    q: 'Як часто потрібно робити професійну чистку зубів?',
    a: 'Зазвичай рекомендуємо 1–2 рази на рік. Частота може змінюватися залежно від стану ясен та швидкості утворення нальоту.'
  }, {
    q: 'Чи боляче робити Airflow або ультразвукову чистку?',
    a: 'Більшість пацієнтів переносить процедуру комфортно. За підвищеної чутливості ми підбираємо більш делікатний режим.'
  }, {
    q: 'Чим відрізняється Airflow від звичайної чистки?',
    a: "Airflow допомагає видалити м'який наліт і пігментацію (кава/чай), а ультразвук — зубний камінь. Часто методики комбінують."
  }, {
    q: 'Чи освітлюються зуби після чистки?',
    a: 'Після зняття нальоту зуби можуть виглядати світлішими, бо забруднення видалені. Але це не замінює професійне відбілювання.'
  }, {
    q: 'Як записатися на гігієну в Angel, London?',
    a: 'Запишіться онлайн або напишіть нам у WhatsApp/Telegram. Dentico Clinic — поруч з Angel Station (EC1V).'
  }],
  whitening: [{
    q: 'Чи безпечне професійне відбілювання зубів?',
    a: 'Так, за умови правильних показань та контролю стоматолога. Перед процедурою ми оцінюємо стан емалі та ясен.'
  }, {
    q: 'Скільки тримається ефект відбілювання?',
    a: 'Тривалість залежить від харчових звичок та догляду. Ми даємо рекомендації, як зберегти результат надовше.'
  }, {
    q: 'Чи буде чутливість після відбілювання?',
    a: 'Може бути тимчасова чутливість. Зазвичай вона проходить швидко, а ми підбираємо догляд та рекомендації для комфорту.'
  }, {
    q: 'Чи можна відбілювати зуби з пломбами/коронками?',
    a: 'Відбілювання не змінює колір пломб і коронок. На консультації ми пояснюємо варіанти, щоб колір виглядав гармонійно.'
  }, {
    q: 'Де зробити відбілювання зубів у Лондоні (Angel)?',
    a: 'Dentico Clinic знаходиться в Angel / Islington, Central London (EC1V). Запис доступний онлайн або через WhatsApp/Telegram.'
  }],
  extraction: [{
    q: 'Що робити, якщо сильно болить зуб?',
    a: "Якнайшвидше зв'яжіться з нами для невідкладного огляду. Ми визначимо причину болю та запропонуємо лікування."
  }, {
    q: 'Чи приймаєте ви терміново в той же день?',
    a: 'Так, за можливості ми пропонуємо найближчий доступний час. Напишіть у WhatsApp/Telegram або зателефонуйте.'
  }, {
    q: 'Скільки коштує екстрений прийом у Лондоні?',
    a: 'Вартість залежить від необхідних процедур та діагностики. Після огляду ми пояснимо варіанти лікування та ціни.'
  }, {
    q: 'Які симптоми вважаються невідкладними?',
    a: 'Сильний біль, набряк, гній, підвищена температура, біль при ковтанні, травма/скол зуба — це привід звернутися терміново.'
  }, {
    q: 'Де ви знаходиться?',
    a: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington), поруч з Angel Station.'
  }],
  'smile-makeover': [{
    q: 'Скільки коштують вініри в Лондоні?',
    a: 'Ціна залежить від матеріалу, кількості зубів та складності естетичного плану. Після консультації ми пропонуємо план лікування та кошторис.'
  }, {
    q: 'Чи потрібно обточувати зуби під вініри?',
    a: 'У більшості випадків потрібна мінімальна підготовка емалі, але обсяг залежить від форми, прикусу та бажаного результату. Ми завжди обираємо максимально щадний підхід.'
  }, {
    q: 'Скільки служать вініри?',
    a: 'Термін служби залежить від матеріалу, прикусу та догляду. Під час консультації ми пояснюємо, як продовжити довговічність роботи.'
  }, {
    q: 'Чи можна зробити природний результат, без "надто білих" зубів?',
    a: 'Так. Ми підбираємо форму та колір індивідуально, щоб усмішка виглядала натурально.'
  }, {
    q: 'Чи можна отримати консультацію українською мовою?',
    a: 'Так, у Dentico Clinic можливе спілкування українською мовою під час консультації та лікування.'
  }],
  cosmetic: [{
    q: 'Скільки коштує імплантація зуба в Лондоні?',
    a: 'Вартість імплантації залежить від клінічної ситуації, типу імпланта та необхідності додаткових процедур (наприклад, кісткова пластика). Після консультації ми складаємо прозорий план лікування та кошторис.'
  }, {
    q: 'Чи боляче ставити імплант?',
    a: 'Процедура проводиться під місцевою анестезією. Під час встановлення імпланта більшість пацієнтів не відчувають болю, а після — можливий дискомфорт, який контролюється рекомендаціями та знеболенням.'
  }, {
    q: 'Скільки часу займає імплантація?',
    a: 'Терміни залежать від випадку. Зазвичай спочатку проводиться консультація та діагностика, потім встановлення імпланта, а після періоду загоєння — встановлення коронки. Точний графік визначається індивідуально.'
  }, {
    q: 'Чи підходить імплантація, якщо давно немає зуба?',
    a: "Так, але може знадобитися оцінка стану кістки. Під час консультації ми перевіряємо об'єм кістки та за потреби обговорюємо варіанти відновлення."
  }, {
    q: 'Де знаходиться клініка для імплантації?',
    a: 'Dentico Clinic розташована в Angel / Islington, Central London (EC1V). Адреса: 360–364 City Road, London EC1V 2PY, поруч з Angel Station.'
  }]
};
function ServiceFaqItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2547">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-3.5 text-left focus:outline-none group" data-id="element-2548">
        <span className="text-base font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2549">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2550">
          {isOpen ? <Minus className="w-3 h-3" data-id="element-2551" /> : <Plus className="w-3 h-3" data-id="element-2552" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-3.5' : 'max-h-0 opacity-0'}`} data-id="element-2553">
        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2554">{answer}</p>
      </div>
    </div>;
}
export function ServiceDetailPage() {
  const {
    serviceId
  } = useParams();
  const {
    t,
    lang,
    getArray
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const servicesData: Record<string, {
    image: string;
  }> = {
    fillings: {
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80'
    },
    'root-canal': {
      image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80'
    },
    crowns: {
      image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=1200&q=80'
    },
    hygiene: {
      image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80'
    },
    whitening: {
      image: 'https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?auto=format&fit=crop&w=1200&q=80'
    },
    extraction: {
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=1200&q=80'
    },
    'smile-makeover': {
      image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80'
    },
    cosmetic: {
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80'
    }
  };
  const translationKey = serviceId?.replace(/-([a-z])/g, g => g[1].toUpperCase());
  if (!serviceId || !servicesData[serviceId]) {
    return <Navigate to={`/${lang}/services`} replace data-id="element-2555" />;
  }
  const serviceImage = servicesData[serviceId].image;
  const title = lang === 'ua' && serviceId && uaServiceTitles[serviceId] ? uaServiceTitles[serviceId] : t(`services.${translationKey}.title`);
  const description = t(`services.${translationKey}.fullDesc`);
  const benefits = getArray(`services.${translationKey}.benefits`);
  const detail = serviceDetails[serviceId];
  const isSmileMakeover = serviceId === 'smile-makeover';
  const labels = {
    included: {
      en: "What's included",
      ua: 'Що входить',
      ru: 'Что включено'
    },
    time: {
      en: 'Duration',
      ua: 'Тривалість',
      ru: 'Длительность'
    },
    comfort: {
      en: 'Comfort & anaesthesia',
      ua: 'Комфорт та анестезія',
      ru: 'Комфорт и анестезия'
    },
    aftercare: {
      en: 'Aftercare',
      ua: 'Догляд після процедури',
      ru: 'Уход после процедуры'
    }
  };
  const beforeAfterCases = [{
    before: 'https://cdn.magicpatterns.com/uploads/shQ5ZRHdqwygEh9jr68EUz/IMG_8186.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/pnzB7AsEyjzSLFCXJDe779/IMG_9004.jpg',
    description: lang === 'en' ? 'Smile transformation with veneers' : lang === 'ua' ? 'Трансформація посмішки вінірами' : 'Трансформация улыбки винирами'
  }, {
    before: 'https://cdn.magicpatterns.com/uploads/9Sh8YYorQi4Z3aQpsAEiEW/IMG_8183.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/6FZYJz1gkBZaYnPMEjSFwB/IMG_9008.jpg',
    description: lang === 'en' ? 'Complete smile makeover' : lang === 'ua' ? 'Повна трансформація посмішки' : 'Полная трансформация улыбки'
  }];
  // Get related services for this service
  const related = serviceId && relatedServices[serviceId] ? relatedServices[serviceId] : [];
  // Get FAQ for this service (UA only)
  const serviceFaqs = serviceId && lang === 'ua' && serviceUaFaqs[serviceId] ? serviceUaFaqs[serviceId] : [];
  // Service structured data
  const serviceSchema = serviceId ? {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: title,
    description: detail ? detail.intro[lang] : description,
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    howPerformed: detail ? detail.comfort[lang] : undefined,
    preparation: detail ? detail.aftercare[lang] : undefined,
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  } : null;
  // FAQ structured data
  const faqSchema = serviceFaqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: serviceFaqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  } : null;
  return <Layout data-id="element-2556">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2557" />
      {/* Structured Data */}
      {serviceSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(serviceSchema)
    }} data-id="element-2558" />}
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-2559" />}
      {/* Full-width hero image */}
      <div className="relative" data-id="element-2560">
        <div className="h-[300px] sm:h-[400px] lg:h-[480px] w-full overflow-hidden" data-id="element-2561">
          <img src={serviceImage} alt={title} className="w-full h-full object-cover" data-id="element-2562" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-2563" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12" data-id="element-2564">
          <div className="max-w-7xl mx-auto" data-id="element-2565">
            <Link to={`/${lang}/services`} className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4 text-sm" data-id="element-2566">
              <ArrowLeft className="w-4 h-4" data-id="element-2567" />
              {t('services.viewAll')}
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-2568">
              {title}
            </h1>
            {/* UA language badge */}
            {lang === 'ua' && <div className="mt-3 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm" data-id="element-2569">
                <Languages className="w-4 h-4" data-id="element-2570" />
                Консультація українською · Angel, Central London
              </div>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2571">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16" data-id="element-2572">
          {/* Main content */}
          <div className="lg:col-span-3 space-y-8" data-id="element-2573">
            {/* Intro */}
            {detail ? <p className="text-lg text-gray-600 leading-relaxed" data-id="element-2574">
                {detail.intro[lang]}
              </p> : <p className="text-lg text-gray-600 leading-relaxed" data-id="element-2575">
                {description}
              </p>}

            {/* What's included */}
            {detail && <div data-id="element-2576">
                <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2577">
                  {labels.included[lang]}
                </h2>
                <div className="space-y-2.5" data-id="element-2578">
                  {detail.included[lang].map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-2579">
                      <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-2580" />
                      <span className="text-sm text-gray-700" data-id="element-2581">{item}</span>
                    </div>)}
                </div>
              </div>}

            {/* Time / Comfort / Aftercare grid */}
            {detail && <div className="grid sm:grid-cols-3 gap-4" data-id="element-2582">
                <div className="bg-teal-50 rounded-xl p-4" data-id="element-2583">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2584">
                    <Clock className="w-4 h-4 text-teal-600" data-id="element-2585" />
                    <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide" data-id="element-2586">
                      {labels.time[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700" data-id="element-2587">{detail.time[lang]}</p>
                </div>
                <div className="bg-teal-50 rounded-xl p-4" data-id="element-2588">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2589">
                    <Heart className="w-4 h-4 text-teal-600" data-id="element-2590" />
                    <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide" data-id="element-2591">
                      {labels.comfort[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700" data-id="element-2592">
                    {detail.comfort[lang]}
                  </p>
                </div>
                <div className="bg-teal-50 rounded-xl p-4" data-id="element-2593">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2594">
                    <ShieldCheck className="w-4 h-4 text-teal-600" data-id="element-2595" />
                    <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide" data-id="element-2596">
                      {labels.aftercare[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700" data-id="element-2597">
                    {detail.aftercare[lang]}
                  </p>
                </div>
              </div>}

            {/* Original benefits (if no detail, show as before) */}
            {!detail && benefits.length > 0 && <div className="grid sm:grid-cols-2 gap-3" data-id="element-2598">
                {benefits.map((benefit, idx) => <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-2599">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-2600" />
                    <span className="text-sm text-gray-700" data-id="element-2601">{benefit}</span>
                  </div>)}
              </div>}
          </div>

          {/* Sidebar - sticky */}
          <div className="lg:col-span-2" data-id="element-2602">
            <div className="lg:sticky lg:top-24 space-y-4" data-id="element-2603">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-2604">
                <Button size="lg" fullWidth className="gap-2" data-id="element-2605">
                  <Calendar className="w-5 h-5" data-id="element-2606" />
                  {t('servicesPage.bookOnlineNow')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-2607">
                <Button variant="outline" size="lg" fullWidth className="gap-2" data-id="element-2608">
                  <MessageCircle className="w-5 h-5 text-green-600" data-id="element-2609" />
                  WhatsApp
                </Button>
              </a>
              <div className="block" data-id="element-2610">
                <Button variant="outline" size="lg" fullWidth className="gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-2611">
                  <Phone className="w-5 h-5 text-teal-600" data-id="element-2612" />
                  {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : 'Заказать звонок'}
                </Button>
              </div>

              <Link to={`/${lang}/prices#${serviceToPriceAnchor[serviceId!] || ''}`} className="flex items-center gap-3 p-4 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors group" data-id="element-2613">
                <div className="p-2 bg-teal-600 rounded-lg text-white" data-id="element-2614">
                  <PoundSterling className="w-5 h-5" data-id="element-2615" />
                </div>
                <div className="flex-1" data-id="element-2616">
                  <p className="font-semibold text-gray-900 text-sm" data-id="element-2617">
                    {t('nav.prices')}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2618">
                    {lang === 'en' ? 'View transparent pricing' : lang === 'ua' ? 'Переглянути ціни' : 'Посмотреть цены'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-teal-600 transition-colors" data-id="element-2619" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Smile Makeover Special Sections */}
      {isSmileMakeover && <>
          {/* Treatments Grid */}
          <div className="bg-gray-50 py-16" data-id="element-2620">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2621">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2622">
                {lang === 'en' ? 'Our Smile Makeover Treatments' : lang === 'ua' ? 'Наші процедури Smile Makeover' : 'Наши процедуры Smile Makeover'}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-2623">
                {[{
              name: lang === 'en' ? 'Porcelain Veneers' : lang === 'ua' ? 'Керамічні вініри' : 'Керамические виниры',
              desc: lang === 'en' ? 'Ultra-thin shells for a perfect smile' : lang === 'ua' ? 'Ультратонкі накладки для ідеальної посмішки' : 'Ультратонкие накладки для идеальной улыбки'
            }, {
              name: lang === 'en' ? 'Composite Bonding' : lang === 'ua' ? 'Композитні накладки' : 'Композитные накладки',
              desc: lang === 'en' ? 'Reshape and restore naturally' : lang === 'ua' ? 'Природна реставрація зубів' : 'Естественная реставрация зубов'
            }, {
              name: lang === 'en' ? 'Teeth Whitening' : lang === 'ua' ? 'Відбілювання зубів' : 'Отбеливание зубов',
              desc: lang === 'en' ? 'Professional brighter smile' : lang === 'ua' ? 'Професійне відбілювання' : 'Профессиональное отбеливание'
            }, {
              name: lang === 'en' ? 'Inlays & Onlays' : lang === 'ua' ? 'Вкладки та накладки' : 'Вкладки и накладки',
              desc: lang === 'en' ? 'Custom ceramic restorations' : lang === 'ua' ? 'Індивідуальні керамічні реставрації' : 'Индивидуальные керамические реставрации'
            }, {
              name: lang === 'en' ? 'Muscle Relaxation' : lang === 'ua' ? "Розслаблення м'язів" : 'Расслабление мышц',
              desc: lang === 'en' ? 'Facial aesthetics complement' : lang === 'ua' ? 'Естетика обличчя' : 'Эстетика лица'
            }, {
              name: lang === 'en' ? '3D Scanning' : lang === 'ua' ? '3D-сканування' : '3D-сканирование',
              desc: lang === 'en' ? 'Digital planning for predictable results' : lang === 'ua' ? 'Цифрове планування результату' : 'Цифровое планирование результата'
            }].map((treatment, idx) => <div key={idx} className="bg-white p-5 rounded-xl border border-gray-100" data-id="element-2624">
                    <Sparkles className="w-6 h-6 text-teal-600 mb-3" data-id="element-2625" />
                    <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2626">
                      {treatment.name}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-2627">{treatment.desc}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Before & After Gallery */}
          <div className="bg-white py-16" data-id="element-2628">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2629">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2630">
                {t('services.smileMakeover.gallery.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-2631">
                {beforeAfterCases.map((caseItem, idx) => <div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100" data-id="element-2632">
                    <div className="grid grid-cols-2" data-id="element-2633">
                      <div className="relative aspect-square" data-id="element-2634">
                        <img src={caseItem.before} alt={`Before - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-2635" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-2636">
                          <span className="text-white text-xs font-medium uppercase tracking-wide" data-id="element-2637">
                            {lang === 'en' ? 'Before' : 'До'}
                          </span>
                        </div>
                      </div>
                      <div className="relative aspect-square" data-id="element-2638">
                        <img src={caseItem.after} alt={`After - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-2639" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-2640">
                          <span className="text-white text-xs font-medium uppercase tracking-wide" data-id="element-2641">
                            {lang === 'en' ? 'After' : lang === 'ua' ? 'Після' : 'После'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 text-center bg-teal-50" data-id="element-2642">
                      <p className="text-sm font-medium text-teal-800" data-id="element-2643">
                        {caseItem.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="bg-gray-50 py-16" data-id="element-2644">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2645">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2646">
                {t('services.smileMakeover.video.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-2647">
                {[1, 2].map((_, idx) => <div key={idx} className="bg-gray-200 rounded-2xl aspect-video flex items-center justify-center overflow-hidden" data-id="element-2648">
                    <div className="text-center" data-id="element-2649">
                      <div className="w-14 h-14 bg-white/80 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-2650">
                        <Play className="w-6 h-6 text-teal-600 ml-0.5" data-id="element-2651" />
                      </div>
                      <p className="text-gray-500 text-sm" data-id="element-2652">
                        {t('services.smileMakeover.video.placeholder')}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </>}

      {/* Related Services */}
      {related.length > 0 && <div className="bg-gray-50 py-12 border-t border-gray-100" data-id="element-2653">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2654">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2655">
              {lang === 'en' ? 'Related Services' : lang === 'ua' ? 'Інші послуги' : 'Другие услуги'}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4" data-id="element-2656">
              {related.map(relId => {
            const relKey = relId.replace(/-([a-z])/g, g => g[1].toUpperCase());
            const relTitle = lang === 'ua' && uaServiceTitles[relId] ? uaServiceTitles[relId] : t(`services.${relKey}.title`);
            return <Link key={relId} to={`/${lang}/services/${relId}`} className="group" data-id="element-2657">
                    <div className="bg-white p-5 rounded-xl border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all" data-id="element-2658">
                      <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1" data-id="element-2659">
                        {relTitle}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-1" data-id="element-2660">
                        {t(`services.${relKey}.desc`)}
                      </p>
                      <span className="text-sm text-teal-600 font-medium mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2661">
                        {t('services.learnMore')}{' '}
                        <ArrowRight className="w-3 h-3" data-id="element-2662" />
                      </span>
                    </div>
                  </Link>;
          })}
            </div>
          </div>
        </div>}

      {/* UA Service FAQ */}
      {serviceFaqs.length > 0 && <div className="bg-white py-12 border-t border-gray-100" data-id="element-2663">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2664">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2665">
              Часті запитання
            </h2>
            <div className="space-y-1" data-id="element-2666">
              {serviceFaqs.map((faq, idx) => <ServiceFaqItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} data-id="element-2667" />)}
            </div>
          </div>
        </div>}

      {/* Callback Form */}
      <div className="py-16 bg-white" data-id="element-2668">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2669">
          <CallbackForm data-id="element-2670" />
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-teal-900 py-16" data-id="element-2671">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-2672">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-2673">
            {t('servicesPage.readyToBook')}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-2674">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2675">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2676">
                <Calendar className="w-5 h-5" data-id="element-2677" />
                {t('servicesPage.bookOnlineNow')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2678">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-2679">
                <MessageCircle className="w-5 h-5" data-id="element-2680" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Layout>;
}